import { useState, useEffect } from 'react';
import { FaUpload, FaTrash, FaSpinner, FaPlus, FaVolumeUp, FaCamera, FaTimes } from 'react-icons/fa';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { extractTextFromImage, generateSentencesFromText } from '../../../utilities/extensions/imageToText';
import { translateBatchToText } from '../../../utilities/extensions/translateToText';
import Constants from '../../../utilities/constants/Constants';
import { generateBatchSpeechAudio, LanguageCodes, VoiceGenders } from '../../../utilities/extensions/textToSpeech';
import { db } from '../../../firebase';
import { generateActivityStoragePath } from '../../../utilities/storagePathHelper';

const WhoSaidToWhomActivity = ({ initialContent, onContentChange, chapterId, grade, selectedChapter }) => {
  const [content, setContent] = useState({
    dialogues: [{
      languageText: '',
      englishText: '',
      languageTextAudio: null,
      englishTextAudio: null,
      whoSaid: '',
      toWhom: '',
      whoSaidEnglish: '',
      toWhomEnglish: '',
      whoConfusingLetters: '',
      whomConfusingLetters: ''
    }]
  });
  const [uploadingFiles, setUploadingFiles] = useState({});
  
  // Image-to-text popup state
  const [showImageToTextPopup, setShowImageToTextPopup] = useState(false);
  const [imageProcessing, setImageProcessing] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imageBlobUrl, setImageBlobUrl] = useState(null);
  const [extractedText, setExtractedText] = useState(''); // eslint-disable-line no-unused-vars
  const [generatedDialogues, setGeneratedDialogues] = useState([]);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [translating, setTranslating] = useState(false);

  // Build storage path using the helper
  const buildStoragePath = ({
    dialogueId,
    fileName
  }) => {
    const language = selectedChapter?.subject?.toLowerCase() || 'language';
    const gradeStr = `grade${grade}`;
    
    return generateActivityStoragePath({
      language,
      grade: gradeStr,
      chapterId,
      activityName: 'who_said_to_whom',
      fileName,
      itemId: dialogueId
    });
  };

  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = JSON.parse(initialContent);
        setContent(parsed);
      } catch {
        // Keep default content if parsing fails
      }
    }
  }, [initialContent]);

  useEffect(() => {
    onContentChange(content);
  }, [content, onContentChange]);

  const addDialogue = () => {
    const newDialogues = [...content.dialogues, {
      languageText: '',
      englishText: '',
      languageTextAudio: null,
      englishTextAudio: null,
      whoSaid: '',
      toWhom: '',
      whoSaidEnglish: '',
      toWhomEnglish: '',
      whoConfusingLetters: '',
      whomConfusingLetters: ''
    }];
    setContent(prev => ({ ...prev, dialogues: newDialogues }));
  };

  const removeDialogue = async (index) => {
    if (content.dialogues.length > 1) {
      const dialogueToDelete = content.dialogues[index];
      const storage = getStorage();
      
      // Delete all associated files from Firebase Storage
      const fileTypes = ['languageTextAudio', 'englishTextAudio'];
      
      for (const fileType of fileTypes) {
        if (dialogueToDelete[fileType]) {
          try {
            const fileRef = ref(storage, dialogueToDelete[fileType]);
            await deleteObject(fileRef);
          } catch (error) {

        // Ignore errors
          }
        }
      }
      
      const newDialogues = content.dialogues.filter((_, i) => i !== index);
      setContent({ ...content, dialogues: newDialogues });
    }
  };

  const updateDialogue = (index, field, value) => {
    const newDialogues = [...content.dialogues];
    newDialogues[index] = { ...newDialogues[index], [field]: value };
    setContent({ ...content, dialogues: newDialogues });
  };

  const uploadFile = async (file, index, fileType) => {
    if (!file || !chapterId || !grade) {
      alert('Missing required information for file upload');
      return;
    }

    const uploadKey = `${index}-${fileType}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const storage = getStorage();
      const dialogueId = `dialogue${index + 1}`;
      
      const storagePath = buildStoragePath({
        dialogueId,
        fileName: file.name
      });

      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      
      updateDialogue(index, fileType, storagePath);
      
    } catch (error) {

      alert(`Failed to upload file: ${error.message}`);
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const removeFile = async (index, fileType) => {
    const dialogue = content.dialogues[index];
    if (dialogue[fileType]) {
      try {
        const storage = getStorage();
        const fileRef = ref(storage, dialogue[fileType]);
        await deleteObject(fileRef);
        updateDialogue(index, fileType, null);
      } catch (error) {

        alert(`Failed to delete file: ${error.message}`);
      }
    }
  };

  const playAudio = async (audioPath) => {
    if (audioPath) {
      try {
        const storage = getStorage();
        const audioRef = ref(storage, audioPath);
        const downloadURL = await getDownloadURL(audioRef);
        const audio = new Audio(downloadURL);
        audio.play().catch(() => {});
      } catch (error) {

        // Ignore errors
      }
    }
  };

  // Image-to-text functions
  const handleImageUploadForText = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadedImage(file);
    
    // Create blob URL for preview
    const blobUrl = URL.createObjectURL(file);
    setImageBlobUrl(blobUrl);
    
    setImageProcessing(true);
    setExtractedText('');
    setGeneratedDialogues([]);

    try {
      // Extract text from image
      const storage = getStorage();
      const text = await extractTextFromImage(file, storage, db);
      setExtractedText(text);
      
      // Generate dialogues from extracted text
      const sentences = generateSentencesFromText(text);
      
      // Convert sentences to dialogue format
      const dialogues = sentences.map(sentence => {
        const languageTextTemp = sentence.languageText || '';
        let languageText = '';
        let whoSaid = '';
        let toWhom = '';
        
        // Try to parse dialogue format: "Speaker: Text" or "Text - Speaker to Recipient"
        if (languageTextTemp.includes(':')) {
          const parts = languageTextTemp.split(':');
          whoSaid = parts[0].trim();
          languageText = parts.slice(1).join(':').trim();
        } else if (languageTextTemp.includes('-')) {
          const parts = languageTextTemp.split('-');
          languageText = parts[0].trim();
          const speakerInfo = parts.slice(1).join('-').trim();
          if (speakerInfo.includes(' to ')) {
            const speakerParts = speakerInfo.split(' to ');
            whoSaid = speakerParts[0].trim();
            toWhom = speakerParts[1].trim();
          } else {
            whoSaid = speakerInfo;
          }
        } else {
          languageText = languageTextTemp;
        }
        
        return {
          languageText,
          englishText: '',
          languageTextAudio: null,
          englishTextAudio: null,
          whoSaid,
          toWhom,
          whoSaidEnglish: '',
          toWhomEnglish: '',
          whoConfusingLetters: '',
          whomConfusingLetters: ''
        };
      });
      
      setGeneratedDialogues(dialogues);
    } catch (error) {

      alert('Failed to process image: ' + error.message);
    } finally {
      setImageProcessing(false);
    }
  };

  const addGeneratedDialogues = () => {
    if (generatedDialogues.length > 0) {
      const newDialogues = [...content.dialogues, ...generatedDialogues];
      setContent({ ...content, dialogues: newDialogues });
      closeImageToTextPopup();
    }
  };

  const closeImageToTextPopup = () => {
    setShowImageToTextPopup(false);
    setUploadedImage(null);
    if (imageBlobUrl) {
      URL.revokeObjectURL(imageBlobUrl);
      setImageBlobUrl(null);
    }
    setExtractedText('');
    setGeneratedDialogues([]);
    setImageProcessing(false);
  };

  const editGeneratedDialogue = (index, field, value) => {
    const updated = [...generatedDialogues];
    updated[index] = { ...updated[index], [field]: value };
    setGeneratedDialogues(updated);
  };

  const removeGeneratedDialogue = (index) => {
    const updated = generatedDialogues.filter((_, i) => i !== index);
    setGeneratedDialogues(updated);
  };

  const addRowAbove = (index) => {
    const newDialogue = {
      languageText: '',
      englishText: '',
      languageTextAudio: null,
      englishTextAudio: null,
      whoSaid: '',
      toWhom: '',
      whoSaidEnglish: '',
      toWhomEnglish: '',
      whoConfusingLetters: '',
      whomConfusingLetters: ''
    };
    const updated = [...generatedDialogues];
    updated.splice(index, 0, newDialogue);
    setGeneratedDialogues(updated);
  };

  // Generate audio for generated dialogues
  const generateAudioForDialogues = async () => {
    if (generatedDialogues.length === 0) return;

    setGeneratingAudio(true);

    try {
      const storage = getStorage();
      const ttsRequests = [];
      
      generatedDialogues.forEach((dialogue, index) => {
        // Add Language text audio request
        if (dialogue.languageText && dialogue.languageText.trim()) {
          ttsRequests.push({
            text: dialogue.languageText,
            languageCode: LanguageCodes.KANNADA,
            ssmlGender: VoiceGenders.FEMALE,
            dialogueIndex: index,
            type: 'languageText'
          });
        }
        
        // Add English text audio request
        if (dialogue.englishText && dialogue.englishText.trim()) {
          ttsRequests.push({
            text: dialogue.englishText,
            languageCode: LanguageCodes.ENGLISH,
            ssmlGender: VoiceGenders.FEMALE,
            dialogueIndex: index,
            type: 'englishText'
          });
        }
      });

      // Generate audio for all requests
      const audioUrls = await generateBatchSpeechAudio(
        ttsRequests.map(req => ({
          text: req.text,
          languageCode: req.languageCode,
          ssmlGender: req.ssmlGender
        })),
        db,
        storage
      );
      
      // Map audio URLs back to dialogues
      const updatedDialogues = [...generatedDialogues];
      
      ttsRequests.forEach((request, requestIndex) => {
        const audioUrl = audioUrls[requestIndex];
        if (audioUrl && !audioUrl.startsWith('[TTS Error:')) {
          const { dialogueIndex, type } = request;
          if (type === 'languageText') {
            updatedDialogues[dialogueIndex].languageTextAudio = audioUrl;
          } else if (type === 'englishText') {
            updatedDialogues[dialogueIndex].englishTextAudio = audioUrl;
          }
        }
      });
      
      setGeneratedDialogues(updatedDialogues);
    } catch (error) {

      alert('Failed to generate audio: ' + error.message);
    } finally {
      setGeneratingAudio(false);
    }
  };

  // Translate Language dialogues to English
  const translateDialogues = async () => {
    if (generatedDialogues.length === 0) return;

    setTranslating(true);

    try {
      // Extract Language texts that don't have English translations
      const dialoguesToTranslate = generatedDialogues
        .map((dialogue, index) => ({ text: dialogue.languageText, index }))
        .filter(item => item.text && item.text.trim() && !generatedDialogues[item.index].englishText);

      if (dialoguesToTranslate.length === 0) {
        alert('All dialogues already have English translations or no Language text to translate.');
        return;
      }

      // Translate the Language texts (Kannada to English)
      const textsToTranslate = dialoguesToTranslate.map(item => item.text);
      const translatedTexts = await translateBatchToText(
        textsToTranslate, 
        db, 
        5, 
        2000, 
        Constants.LANGUAGES.KANNADA, 
        Constants.LANGUAGES.ENGLISH
      );
      
      // Update dialogues with English translations
      const updatedDialogues = [...generatedDialogues];
      dialoguesToTranslate.forEach((item, translationIndex) => {
        const translatedText = translatedTexts[translationIndex];
        if (translatedText && !translatedText.startsWith('[Translation Error:')) {
          updatedDialogues[item.index].englishText = translatedText;
        }
      });
      
      setGeneratedDialogues(updatedDialogues);
    } catch (error) {

      alert('Failed to translate dialogues: ' + error.message);
    } finally {
      setTranslating(false);
    }
  };

  const validate = () => {
    const errors = [];
    
    if (!content.dialogues || content.dialogues.length === 0) {
      errors.push('At least one dialogue is required');
    }

    content.dialogues?.forEach((dialogue, index) => {
      if (!dialogue.languageText?.trim()) {
        errors.push(`Language text is required for dialogue ${index + 1}`);
      }
      if (!dialogue.englishText?.trim()) {
        errors.push(`English text is required for dialogue ${index + 1}`);
      }
      if (!dialogue.whoSaid?.trim()) {
        errors.push(`"Who said" is required for dialogue ${index + 1}`);
      }
      if (!dialogue.toWhom?.trim()) {
        errors.push(`"To whom" is required for dialogue ${index + 1}`);
      }
    });

    return errors;
  };

  return (
    <div className="space-y-6">
      {/* Dialogues Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              💬 <span className="ml-2">Who Said To Whom Dialogues</span>
            </h3>
            <p className="text-sm text-gray-600 mt-1">{content.dialogues.length} dialogue{content.dialogues.length !== 1 ? 's' : ''} configured</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium shadow-sm"
              onClick={() => setShowImageToTextPopup(true)}
            >
              <FaCamera className="w-4 h-4 mr-2" />
              Extract from Image
            </button>
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
              onClick={addDialogue}
            >
              <FaPlus className="w-4 h-4 mr-2" />
              Add Dialogue
            </button>
          </div>
        </div>
        <div className="p-6 space-y-6">
          {content.dialogues.map((dialogue, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                    {index + 1}
                  </div>
                  <h4 className="text-lg font-medium text-gray-900">Dialogue {index + 1}</h4>
                </div>
                {content.dialogues.length > 1 && (
                  <button
                    type="button"
                    className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors"
                    onClick={() => removeDialogue(index)}
                    title="Remove Dialogue"
                  >
                    <FaTrash className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-4">
                {/* Language Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Language Text *
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter Language dialogue..."
                    value={dialogue.languageText || ''}
                    onChange={(e) => updateDialogue(index, 'languageText', e.target.value)}
                  />
                </div>

                {/* English Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    English Text *
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter English dialogue..."
                    value={dialogue.englishText || ''}
                    onChange={(e) => updateDialogue(index, 'englishText', e.target.value)}
                  />
                </div>

                {/* Who Said */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Who Said *
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="e.g., ಶಿಕ್ಷಕ, ತಾಯಿ..."
                    value={dialogue.whoSaid || ''}
                    onChange={(e) => updateDialogue(index, 'whoSaid', e.target.value)}
                  />
                </div>

                {/* To Whom */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To Whom *
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="e.g., ವಿದ್ಯಾರ್ಥಿಗಳು, ಮಗು..."
                    value={dialogue.toWhom || ''}
                    onChange={(e) => updateDialogue(index, 'toWhom', e.target.value)}
                  />
                </div>

                {/* Who Said English */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Who Said Eng
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="e.g., Teacher, Mother..."
                    value={dialogue.whoSaidEnglish || ''}
                    onChange={(e) => updateDialogue(index, 'whoSaidEnglish', e.target.value)}
                  />
                </div>

                {/* To Whom English */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To Whom Eng
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="e.g., Students, Child..."
                    value={dialogue.toWhomEnglish || ''}
                    onChange={(e) => updateDialogue(index, 'toWhomEnglish', e.target.value)}
                  />
                </div>

                {/* Who Confusing Letters */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Who Conf Let
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Alternative spellings..."
                    value={dialogue.whoConfusingLetters || ''}
                    onChange={(e) => updateDialogue(index, 'whoConfusingLetters', e.target.value)}
                  />
                </div>

                {/* Whom Confusing Letters */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Whom Conf Let
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Alternative spellings..."
                    value={dialogue.whomConfusingLetters || ''}
                    onChange={(e) => updateDialogue(index, 'whomConfusingLetters', e.target.value)}
                  />
                </div>
              </div>

              {/* Audio Files */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Language Audio */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Language Audio
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      id={`language-audio-${index}`}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) uploadFile(file, index, 'languageTextAudio');
                      }}
                      disabled={uploadingFiles[`${index}-languageTextAudio`]}
                    />
                    <label
                      htmlFor={`language-audio-${index}`}
                      className="cursor-pointer flex items-center px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                    >
                      <FaUpload className="w-4 h-4 mr-2" />
                      Upload Audio
                    </label>
                    {uploadingFiles[`${index}-languageTextAudio`] && (
                      <FaSpinner className="w-4 h-4 animate-spin text-blue-600" />
                    )}
                    {dialogue.languageTextAudio && !uploadingFiles[`${index}-languageTextAudio`] && (
                      <>
                        <button
                          type="button"
                          className="p-2 text-green-600 hover:bg-green-100 rounded"
                          onClick={() => playAudio(dialogue.languageTextAudio)}
                          title="Play Language Audio"
                        >
                          <FaVolumeUp className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          className="p-2 text-red-600 hover:bg-red-100 rounded"
                          onClick={() => removeFile(index, 'languageTextAudio')}
                          title="Remove Language Audio"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* English Audio */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    English Audio
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      id={`english-audio-${index}`}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) uploadFile(file, index, 'englishTextAudio');
                      }}
                      disabled={uploadingFiles[`${index}-englishTextAudio`]}
                    />
                    <label
                      htmlFor={`english-audio-${index}`}
                      className="cursor-pointer flex items-center px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium"
                    >
                      <FaUpload className="w-4 h-4 mr-2" />
                      Upload Audio
                    </label>
                    {uploadingFiles[`${index}-englishTextAudio`] && (
                      <FaSpinner className="w-4 h-4 animate-spin text-green-600" />
                    )}
                    {dialogue.englishTextAudio && !uploadingFiles[`${index}-englishTextAudio`] && (
                      <>
                        <button
                          type="button"
                          className="p-2 text-green-600 hover:bg-green-100 rounded"
                          onClick={() => playAudio(dialogue.englishTextAudio)}
                          title="Play English Audio"
                        >
                          <FaVolumeUp className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          className="p-2 text-red-600 hover:bg-red-100 rounded"
                          onClick={() => removeFile(index, 'englishTextAudio')}
                          title="Remove English Audio"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Image-to-Text Popup */}
      {showImageToTextPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Extract Dialogues from Image</h3>
                <p className="text-sm text-gray-600 mt-1">Upload an image to automatically extract dialogue content</p>
              </div>
              <button
                onClick={closeImageToTextPopup}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {!uploadedImage ? (
                /* Upload Section */
                <div className="text-center">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 hover:border-emerald-400 transition-colors">
                    <div className="flex flex-col items-center">
                      <FaCamera className="w-12 h-12 text-gray-400 mb-4" />
                      <label htmlFor="imageToTextUpload" className="text-lg font-medium text-gray-900 mb-2 cursor-pointer">
                        Upload an image to extract dialogues
                      </label>
                      <input
                        type="file"
                        id="imageToTextUpload"
                        className="hidden"
                        accept="image/*"
                        onChange={handleImageUploadForText}
                        disabled={imageProcessing}
                      />
                      <button
                        type="button"
                        className="bg-emerald-500 hover:bg-emerald-600 text-white font-medium py-3 px-6 rounded-lg transition-colors disabled:bg-emerald-400 disabled:cursor-not-allowed mb-3"
                        onClick={() => document.getElementById('imageToTextUpload').click()}
                        disabled={imageProcessing}
                      >
                        Choose Image File
                      </button>
                      <p className="text-sm text-gray-500">
                        Supported formats: JPG, PNG, GIF. Use format: &quot;Speaker: Text&quot; or &quot;Text - Speaker to Recipient&quot; for best results.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                /* Processing and Results Section */
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Uploaded Image */}
                  <div className="lg:col-span-1">
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <FaCamera className="text-blue-600" />
                        Uploaded Image
                      </h4>
                      <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                        <img 
                          src={imageBlobUrl} 
                          alt="Uploaded" 
                          className="w-full h-auto rounded-lg shadow-sm"
                          style={{ maxHeight: '300px', objectFit: 'contain' }}
                        />
                      </div>
                      <button 
                        className="w-full mt-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg border border-gray-300 transition-colors flex items-center justify-center gap-2 text-sm"
                        onClick={() => {
                          closeImageToTextPopup();
                        }}
                      >
                        <FaUpload className="w-4 h-4" />
                        Upload Different Image
                      </button>
                    </div>
                  </div>
                  
                  {/* Generated Dialogues */}
                  <div className="lg:col-span-2">
                    {imageProcessing ? (
                      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                        <div className="flex flex-col items-center gap-4">
                          <FaSpinner className="w-12 h-12 animate-spin text-emerald-500" />
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900 mb-1">Processing Image</h4>
                            <p className="text-gray-600 text-sm">Analyzing image and extracting dialogues using AI...</p>
                          </div>
                        </div>
                      </div>
                    ) : generatedDialogues.length > 0 ? (
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                          <div>
                            <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                              ✨ Generated dialogues ({generatedDialogues.length})
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button 
                              className="bg-purple-500 hover:bg-purple-600 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:bg-purple-400 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                              onClick={translateDialogues}
                              disabled={translating || generatingAudio}
                            >
                              {translating ? (
                                <>
                                  <FaSpinner className="w-4 h-4 animate-spin" />
                                  Translating...
                                </>
                              ) : (
                                <>
                                  🌐 Translate Text
                                </>
                              )}
                            </button>
                            <button 
                              className="bg-orange-500 hover:bg-orange-600 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:bg-orange-400 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                              onClick={generateAudioForDialogues}
                              disabled={generatingAudio || translating}
                            >
                              {generatingAudio ? (
                                <>
                                  <FaSpinner className="w-4 h-4 animate-spin" />
                                  Generating Audio...
                                </>
                              ) : (
                                <>
                                  🔊 Generate Audio
                                </>
                              )}
                            </button>
                            <button 
                              className="bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center gap-2 text-sm"
                              onClick={addGeneratedDialogues}
                            >
                              <FaPlus className="w-4 h-4" />
                              Add All Dialogues
                            </button>
                          </div>
                        </div>
                        
                        <div className="space-y-4 max-h-96 overflow-y-auto">
                          {generatedDialogues.map((dialogue, index) => (
                            <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                              {/* First Row - Main Content */}
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Language Text</label>
                                  <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    value={dialogue.languageText}
                                    onChange={(e) => editGeneratedDialogue(index, 'languageText', e.target.value)}
                                    placeholder="Language dialogue..."
                                  />
                                  {dialogue.languageTextAudio && (
                                    <div className="mt-2">
                                      <button
                                        type="button"
                                        className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 hover:bg-green-50 px-2 py-1 rounded transition-colors"
                                        onClick={() => playAudio(dialogue.languageTextAudio)}
                                      >
                                        <FaVolumeUp className="w-3 h-3" />
                                        Play Audio
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">English Text</label>
                                  <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    value={dialogue.englishText}
                                    onChange={(e) => editGeneratedDialogue(index, 'englishText', e.target.value)}
                                    placeholder="English translation..."
                                  />
                                  {dialogue.englishTextAudio && (
                                    <div className="mt-2">
                                      <button
                                        type="button"
                                        className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 hover:bg-green-50 px-2 py-1 rounded transition-colors"
                                        onClick={() => playAudio(dialogue.englishTextAudio)}
                                      >
                                        <FaVolumeUp className="w-3 h-3" />
                                        Play Audio
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Who Said</label>
                                  <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    value={dialogue.whoSaid}
                                    onChange={(e) => editGeneratedDialogue(index, 'whoSaid', e.target.value)}
                                    placeholder="Speaker..."
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">To Whom</label>
                                  <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    value={dialogue.toWhom}
                                    onChange={(e) => editGeneratedDialogue(index, 'toWhom', e.target.value)}
                                    placeholder="Recipient..."
                                  />
                                </div>
                              </div>
                              
                              {/* Second Row - Additional Fields */}
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Who Said Eng</label>
                                  <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    value={dialogue.whoSaidEnglish || ''}
                                    onChange={(e) => editGeneratedDialogue(index, 'whoSaidEnglish', e.target.value)}
                                    placeholder="Speaker (English)..."
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">To Whom Eng</label>
                                  <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    value={dialogue.toWhomEnglish || ''}
                                    onChange={(e) => editGeneratedDialogue(index, 'toWhomEnglish', e.target.value)}
                                    placeholder="Recipient (English)..."
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Who Conf Let</label>
                                  <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    value={dialogue.whoConfusingLetters || ''}
                                    onChange={(e) => editGeneratedDialogue(index, 'whoConfusingLetters', e.target.value)}
                                    placeholder="Alt spellings..."
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Whom Conf Let</label>
                                  <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    value={dialogue.whomConfusingLetters || ''}
                                    onChange={(e) => editGeneratedDialogue(index, 'whomConfusingLetters', e.target.value)}
                                    placeholder="Alt spellings..."
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end gap-1">
                                <button 
                                  className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 p-2 rounded-lg transition-colors"
                                  onClick={() => addRowAbove(index)}
                                  title="Add row above this dialogue"
                                >
                                  <FaPlus className="w-4 h-4" />
                                </button>
                                <button 
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
                                  onClick={() => removeGeneratedDialogue(index)}
                                  title="Remove this dialogue"
                                >
                                  <FaTrash className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <span className="text-4xl">💬</span>
                          <div>
                            <h4 className="text-lg font-semibold text-blue-900 mb-1">No dialogues generated</h4>
                            <p className="text-blue-700 text-sm">The image processing didn&apos;t generate dialogues. Try uploading a different image with clearer text in dialogue format.</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Validation Display */}
      {(() => {
        const errors = validate();
        if (errors.length > 0) {
          return (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <div className="flex items-start">
                <div className="w-6 h-6 text-amber-600 mr-3 mt-0.5">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-amber-800 mb-2">Please fix the following issues:</h3>
                  <ul className="space-y-1">
                    {errors.map((error, index) => (
                      <li key={index} className="flex items-start text-amber-700">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                        <span>{error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}
    </div>
  );
};

// Export validation function separately for use in parent component
export const validateWhoSaidToWhomContent = (content) => {
  const errors = [];
  
  if (!content.dialogues || content.dialogues.length === 0) {
    errors.push('At least one dialogue is required');
  }

  content.dialogues?.forEach((dialogue, index) => {
    if (!dialogue.languageText?.trim()) {
      errors.push(`Language text is required for dialogue ${index + 1}`);
    }
    if (!dialogue.englishText?.trim()) {
      errors.push(`English text is required for dialogue ${index + 1}`);
    }
    if (!dialogue.whoSaid?.trim()) {
      errors.push(`"Who said" is required for dialogue ${index + 1}`);
    }
    if (!dialogue.toWhom?.trim()) {
      errors.push(`"To whom" is required for dialogue ${index + 1}`);
    }
    
    // Validate English text: must start with capital letter
    if (dialogue.englishText?.trim()) {
      const trimmed = dialogue.englishText.trim();
      if (!/^[A-Z]/.test(trimmed)) {
        errors.push(`English text for dialogue ${index + 1} must start with a capital letter`);
      }
    }
  });

  return errors;
};

export default WhoSaidToWhomActivity;
